import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { FileText, ArrowRight } from 'lucide-react';
import { rfqsApi, categoriesApi, countriesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const CreateRFQPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    product_name: '',
    category: '',
    description: '',
    specifications: '',
    quantity: '',
    quantity_unit: 'pieces',
    budget_min: '',
    budget_max: '',
    budget_currency: 'USD',
    destination_country: '',
    payment_terms: '',
    delivery_terms: '',
    deadline: '',
    is_urgent: false
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/rfqs/create' } } });
      return;
    }

    const fetchData = async () => {
      try {
        const [categoriesRes, countriesRes] = await Promise.all([
          categoriesApi.getAll(),
          countriesApi.getAll()
        ]);
        setCategories(categoriesRes.data || []);
        setCountries(countriesRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        quantity: parseInt(formData.quantity),
        budget_min: formData.budget_min ? parseFloat(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseFloat(formData.budget_max) : null
      };

      const { data } = await rfqsApi.create(submitData);
      toast.success('Buy requirement posted successfully!');
      navigate(`/rfqs/${data.rfq_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post requirement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />

      <div className="container-main py-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#EA580C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-[#EA580C]" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Post Buy Requirement</h1>
            <p className="text-slate-600">
              Let suppliers know what you're looking for and receive quotes from verified manufacturers
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-xl p-6 space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h2 className="font-semibold text-slate-900 border-b pb-2">Product Information</h2>
              
              <div>
                <Label htmlFor="title">Request Title *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Looking for High Power Rectifier Diodes"
                  required
                  className="mt-1"
                  data-testid="rfq-title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="product_name">Product Name *</Label>
                  <Input
                    id="product_name"
                    name="product_name"
                    value={formData.product_name}
                    onChange={handleChange}
                    placeholder="e.g., Rectifier Diodes 1000V"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(val) => setFormData({ ...formData, category: val })}
                  >
                    <SelectTrigger className="mt-1" data-testid="rfq-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe your requirements in detail..."
                  rows={4}
                  required
                  className="mt-1"
                  data-testid="rfq-description-input"
                />
              </div>

              <div>
                <Label htmlFor="specifications">Specifications</Label>
                <Textarea
                  id="specifications"
                  name="specifications"
                  value={formData.specifications}
                  onChange={handleChange}
                  placeholder="Technical specifications, dimensions, materials, etc."
                  rows={3}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Quantity & Budget */}
            <div className="space-y-4">
              <h2 className="font-semibold text-slate-900 border-b pb-2">Quantity & Budget</h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="quantity">Quantity *</Label>
                  <Input
                    id="quantity"
                    name="quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={handleChange}
                    placeholder="e.g., 1000"
                    required
                    className="mt-1"
                    data-testid="rfq-quantity-input"
                  />
                </div>
                <div>
                  <Label htmlFor="quantity_unit">Unit</Label>
                  <Select 
                    value={formData.quantity_unit} 
                    onValueChange={(val) => setFormData({ ...formData, quantity_unit: val })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pieces">Pieces</SelectItem>
                      <SelectItem value="sets">Sets</SelectItem>
                      <SelectItem value="kg">Kilograms</SelectItem>
                      <SelectItem value="tons">Tons</SelectItem>
                      <SelectItem value="meters">Meters</SelectItem>
                      <SelectItem value="containers">Containers</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="budget_min">Budget Min</Label>
                  <Input
                    id="budget_min"
                    name="budget_min"
                    type="number"
                    step="0.01"
                    value={formData.budget_min}
                    onChange={handleChange}
                    placeholder="e.g., 10.00"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="budget_max">Budget Max</Label>
                  <Input
                    id="budget_max"
                    name="budget_max"
                    type="number"
                    step="0.01"
                    value={formData.budget_max}
                    onChange={handleChange}
                    placeholder="e.g., 20.00"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="budget_currency">Currency</Label>
                  <Select 
                    value={formData.budget_currency} 
                    onValueChange={(val) => setFormData({ ...formData, budget_currency: val })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                      <SelectItem value="AED">AED</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Shipping & Terms */}
            <div className="space-y-4">
              <h2 className="font-semibold text-slate-900 border-b pb-2">Shipping & Terms</h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="destination_country">Destination Country *</Label>
                  <Select 
                    value={formData.destination_country} 
                    onValueChange={(val) => setFormData({ ...formData, destination_country: val })}
                  >
                    <SelectTrigger className="mt-1" data-testid="rfq-country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="deadline">Deadline</Label>
                  <Input
                    id="deadline"
                    name="deadline"
                    type="date"
                    value={formData.deadline}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="payment_terms">Payment Terms</Label>
                  <Input
                    id="payment_terms"
                    name="payment_terms"
                    value={formData.payment_terms}
                    onChange={handleChange}
                    placeholder="e.g., T/T, L/C"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="delivery_terms">Delivery Terms</Label>
                  <Input
                    id="delivery_terms"
                    name="delivery_terms"
                    value={formData.delivery_terms}
                    onChange={handleChange}
                    placeholder="e.g., FOB, CIF"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                <div>
                  <p className="font-medium text-red-900">Mark as Urgent</p>
                  <p className="text-sm text-red-700">Urgent requests get priority visibility</p>
                </div>
                <Switch
                  checked={formData.is_urgent}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_urgent: checked })}
                  data-testid="rfq-urgent-switch"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#EA580C] hover:bg-[#C2410C]" 
              disabled={loading}
              data-testid="rfq-submit-btn"
            >
              {loading ? 'Posting...' : 'Post Buy Requirement'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CreateRFQPage;
